<template>
  <div class="material-selector" v-if="availableMaterials.length > 0">
    <!-- <h3>{{ currentMaterialName }}</h3> -->
    <div class="material-grid">
      <div
        v-for="material in availableMaterials"
        :key="material.id"
        class="material-item"
      >
        <div
          @click="!isLoading && selectMaterial(material.id)"
          :class="[
            'material-preview',
            {
              active: currentMaterial === material.id,
              disabled: isLoading
            }
          ]"
          :style="getMaterialStyle(material.id)"
          :aria-label="material.displayName"
        ></div>
        <div class="material-label">
          {{ material.displayName }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { KnobMaterial } from '@/config/knobConfig';

export default defineComponent({
  name: 'MaterialSelector',
  props: {
    currentMaterial: {
      type: String,
      required: true
    },
    availableMaterials: {
      type: Array as () => KnobMaterial[],
      required: true
    },
    selectedKnobTypeName: {
      type: String,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['materialChanged'],
  setup(props, { emit }) {
    const currentMaterialName = computed(() => {
      const material = props.availableMaterials.find(m => m.id === props.currentMaterial);
      return material?.displayName.toUpperCase() || '';
    });

    const selectMaterial = (materialId: string) => {
      if (props.isLoading) return; // Prevent selection during loading
      emit('materialChanged', materialId);
    };

    const KNOB_MATERIAL_STYLES: { [key: string]: any } = {
      // Vintage knob materials
      'vintage_offwhite': {
        backgroundImage: 'url(/buttons/whitegloss.png)',
        backgroundSize: '115%',
        backgroundPosition: '45% 40%',
        backgroundRepeat: 'no-repeat',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'vintage_copper': {
        backgroundImage: 'url(/buttons/gloss%20black.png)',
        backgroundSize: '120%',
        backgroundPosition: '50% 60%',
        backgroundRepeat: 'no-repeat',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'vintage_steel': {
        backgroundImage: 'url(/buttons/gloss%20cream.png)',
        backgroundSize: '120%',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'no-repeat',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },

      // Classic knob materials
      'classic_black': {
        backgroundImage: 'url(/buttons/whitegloss.png)',
        backgroundSize: '115%',
        backgroundPosition: '45% 40%',
        backgroundRepeat: 'no-repeat',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'classic_white': {
        backgroundImage: 'url(/buttons/gloss%20black.png)',
        backgroundSize: '120%',
        backgroundPosition: '50% 60%',
        backgroundRepeat: 'no-repeat',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'classic_silver': {
        backgroundImage: 'url(/buttons/gloss%20cream.png)',
        backgroundSize: '120%',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'no-repeat',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'classic_blue': {
        background: 'rgb(50, 116, 230)',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'classic_bronze': {
        background: 'rgb(240, 50, 50)',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'classic_navy': {
        background: 'rgb(128, 128, 138)',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'classic_chrome': {
        background: 'rgb(40, 60, 160)',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'classic_platinum': {
        background: 'rgb(64, 27, 103)',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      }
    };

    const getMaterialStyle = (materialId: string) => {
      return KNOB_MATERIAL_STYLES[materialId] || {};
    };

    return {
      currentMaterialName,
      selectMaterial,
      getMaterialStyle
    };
  }
});
</script>

<style scoped>
.material-selector {
  margin-bottom: 5.442rem;
}

h3 {
  font-size: 0.8rem;
  margin-top: 0.2rem;
  margin-bottom: 1rem;
  color:rgb(0, 0, 0);
  font-weight: 300;
  letter-spacing: 0.015em;
}

.material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, 95px);
  /* Match Enclosure button horizontal spacing */
  column-gap: 1.56rem;
  row-gap: 1.2rem;
  align-items: start;
}

.material-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.638rem; /* 1.365rem * 1.20 */
}

.material-preview {
  width: 95px;
  height: 95px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  /* Let the active ring render outside the circle */
  overflow: visible;
  /* Ring tuning */
  --active-ring-offset: 8.5px;
  --active-ring-width: 3px;
}

.material-preview.active::after {
  content: '';
  position: absolute;
  left: calc(-1 * var(--active-ring-offset));
  top: calc(-1 * var(--active-ring-offset));
  right: calc(-1 * var(--active-ring-offset));
  bottom: calc(-1 * var(--active-ring-offset));
  border: var(--active-ring-width) solid rgb(0, 0, 0);
  border-radius: 50%;
  pointer-events: none;
}

.material-preview.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.material-label {
  font-family: "Cormorant Garamond", "Times New Roman", Times, serif;
  font-weight: 300;
  font-size: 1.5273rem; /* 1.3281rem * 1.15 */
  line-height: 1.05;
  color: rgb(0, 0, 0);
  text-align: center;
  letter-spacing: 0.005em;
  max-width: 90px;
}

/* Mobile responsive */
@media screen and (max-width: 768px) {
  .material-selector {
    margin-bottom: calc(5.442rem - 26px);
  }

  .material-grid {
    grid-template-columns: repeat(4, 1fr);
    /* Match Enclosure button horizontal spacing */
    column-gap: 1.4458rem;
    row-gap: 1.452rem;
  }

  .material-item {
    gap: 0.9804rem;
  }

  .material-label {
    font-size: 1.2982rem;
  }
  
  .material-preview {
    width: 70px;
    height: 70px;
  }
  
  h2 {
    font-size: 2.2rem; /* 2rem * 1.10 */
  }
  
  h3 {
    font-size: 0.63rem;
  }
}
</style> 